import React, { useMemo, useState } from 'react';
import { FileText, Image as ImageIcon, Table2, Check, Circle } from 'lucide-react';
import type { AnnotationFileKind } from '../../utils/projectHubUnreadItems';
import { formatDistanceToNow } from 'date-fns';
import type { CRMUser, PdfAnnotation } from '../../types';
import {
  markAnnotationAsRead,
  markAnnotationAsUnread,
  pdfAnnotationHasUnreadForUser,
} from '../../utils/pdfAnnotationService';
import {
  displayFileNameFromAnnotation,
  fileKindFromAnnotation,
} from '../../utils/projectHubUnreadItems';

export interface GroupPdfCommentsTabProps {
  annotations: PdfAnnotation[];
  userId: string;
  crmUsers: CRMUser[];
  onOpenPdf?: (
    url: string,
    title: string,
    documentId: string,
    options?: { annotationId?: string; pageNumber?: number },
  ) => void;
  onOpenImage?: (
    url: string,
    title: string,
    documentId: string,
    options?: { annotationId?: string; mimeType?: string },
  ) => void;
  onOpenCsv?: (
    url: string,
    title: string,
    documentId: string,
    options?: { annotationId?: string; mimeType?: string },
  ) => void;
}

/** Latest activity (root or any reply) timestamp + author + text snippet. */
function latestActivity(ann: PdfAnnotation): { ms: number; author: string; text: string } {
  const rootMs = ann.createdAt instanceof Date ? ann.createdAt.getTime() : new Date(ann.createdAt).getTime();
  let bestMs = rootMs;
  let author = ann.userName || '';
  let text = ann.content || '';
  for (const r of ann.replies ?? []) {
    const t = r.createdAt instanceof Date ? r.createdAt.getTime() : new Date(r.createdAt).getTime();
    if (t > bestMs) {
      bestMs = t;
      author = r.userName || author;
      text = r.content || text;
    }
  }
  return { ms: bestMs, author, text };
}

function snippet(text: string, max = 120): string {
  const t = (text || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return 'PDF comment';
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/** Project-scoped list of every PDF comment, with deep links and a per-row read/unread toggle. */
const GroupPdfCommentsTab: React.FC<GroupPdfCommentsTabProps> = ({
  annotations,
  userId,
  crmUsers,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
}) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const canOpen = !!(onOpenPdf || onOpenImage || onOpenCsv);

  const rows = useMemo(() => {
    return annotations
      .filter((a) => a.id)
      .map((a) => {
        const latest = latestActivity(a);
        return {
          ann: a,
          unread: pdfAnnotationHasUnreadForUser(a, userId),
          fileName: displayFileNameFromAnnotation(a),
          kind: fileKindFromAnnotation(a),
          ...latest,
        };
      })
      .sort((a, b) => b.ms - a.ms);
  }, [annotations, userId]);

  const authorName = (raw: string) => {
    const byId = crmUsers.find((u) => u.uid === raw);
    return byId?.displayName || raw || 'Someone';
  };

  const openAnnotation = (ann: PdfAnnotation, kind: AnnotationFileKind) => {
    const url = ann.documentUrl || ann.documentId;
    const documentId = ann.documentId || url;
    const title = displayFileNameFromAnnotation(ann);
    if (kind === 'image') {
      onOpenImage?.(url, title, documentId, { annotationId: ann.id });
    } else if (kind === 'csv') {
      onOpenCsv?.(url, title, documentId, { annotationId: ann.id, mimeType: 'text/csv' });
    } else {
      onOpenPdf?.(url, title, documentId, { annotationId: ann.id, pageNumber: ann.pageNumber });
    }
  };

  const toggleRead = async (ann: PdfAnnotation, currentlyUnread: boolean) => {
    if (!ann.id || !userId) return;
    setBusyId(ann.id);
    try {
      if (currentlyUnread) {
        await markAnnotationAsRead(ann.id, userId);
      } else {
        await markAnnotationAsUnread(ann.id, userId);
      }
    } catch (err) {
      console.error('Error toggling PDF comment read state:', err);
    } finally {
      setBusyId(null);
    }
  };

  if (rows.length === 0) {
    return <div className="tc-empty">No PDF comments on this project yet.</div>;
  }

  return (
    <div className="tc-pdf-tab" role="list">
      {rows.map(({ ann, unread, fileName, kind, author, text, ms }) => (
        <div
          key={ann.id}
          id={ann.id ? `hub-pdf-comment-${ann.id}` : undefined}
          role="listitem"
          className={`tc-pdf-row${unread ? ' tc-pdf-row--unread' : ''}`}
        >
          <div
            className={`tc-pdf-row-click${canOpen ? ' tc-pdf-row-click--active' : ''}`}
            {...(canOpen
              ? {
                  role: 'button',
                  tabIndex: 0,
                  title: 'Go to this comment on the file',
                  'aria-label': `Go to comment on ${fileName}`,
                  onClick: () => openAnnotation(ann, kind),
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openAnnotation(ann, kind);
                    }
                  },
                }
              : {})}
          >
            <div className="tc-pdf-row-icon" aria-hidden>
              {kind === 'image' ? (
                <ImageIcon size={14} />
              ) : kind === 'csv' ? (
                <Table2 size={14} />
              ) : (
                <FileText size={14} />
              )}
            </div>
            <div className="tc-pdf-row-body">
              <div className="tc-pdf-row-meta">
                {unread && <span className="tc-pdf-row-dot" aria-hidden />}
                <span className="tc-pdf-row-file" title={fileName}>{fileName}</span>
                {ann.pageNumber != null && <span className="tc-pdf-row-page">p.{ann.pageNumber}</span>}
                <span className="tc-pdf-row-time">{formatDistanceToNow(new Date(ms), { addSuffix: true })}</span>
              </div>
              <div className="tc-pdf-row-text">{snippet(text || ann.content)}</div>
              <div className="tc-pdf-row-author">{authorName(author || ann.userName)}</div>
            </div>
          </div>
          <div className="tc-pdf-row-actions">
            <button
              type="button"
              className="tc-pdf-row-readbtn"
              disabled={busyId === ann.id}
              onClick={(e) => {
                e.stopPropagation();
                void toggleRead(ann, unread);
              }}
              title={unread ? 'Mark as read' : 'Mark as unread'}
              aria-label={unread ? 'Mark as read' : 'Mark as unread'}
            >
              {unread ? <Check size={13} /> : <Circle size={13} />}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GroupPdfCommentsTab;
